import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, X, SlidersHorizontal } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import type { PlacesFilterState } from "@/hooks/useAdminPlacesList";

interface Props {
  filters: PlacesFilterState;
  setSearch: (v: string) => void;
  setFilter: <K extends keyof PlacesFilterState>(key: K, value: PlacesFilterState[K]) => void;
  resetFilters: () => void;
  activeFilterCount: number;
  filterOptions: { landskap: string[]; municipality: string[] };
}

const STATUS_LABELS: Record<string, string> = {
  all: "Alla",
  published: "Publicerad",
  draft: "Utkast",
  archived: "Arkiverad",
};

export function AdminPlacesFilters({
  filters,
  setSearch,
  setFilter,
  resetFilters,
  activeFilterCount,
  filterOptions,
}: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const chips: { label: string; onRemove: () => void }[] = [];

  if (filters.status !== "all") {
    chips.push({
      label: `Status: ${STATUS_LABELS[filters.status] ?? filters.status}`,
      onRemove: () => setFilter("status", "all"),
    });
  }
  if (filters.landskap !== "all") {
    chips.push({
      label: `Landskap: ${filters.landskap}`,
      onRemove: () => setFilter("landskap", "all"),
    });
  }
  if (filters.municipality !== "all") {
    chips.push({
      label: `Ort: ${filters.municipality}`,
      onRemove: () => setFilter("municipality", "all"),
    });
  }
  if (filters.search.trim()) {
    chips.push({
      label: `Sök: "${filters.search}"`,
      onRemove: () => setSearch(""),
    });
  }

  // ── Desktop filters (unchanged) ──
  const desktopFilters = (
    <div className="hidden md:flex flex-row gap-2 flex-wrap">
      {/* Search */}
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Sök platser…"
          value={filters.search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-8"
        />
        {filters.search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Status */}
      <Select value={filters.status} onValueChange={(v) => setFilter("status", v)}>
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent className="bg-popover">
          <SelectItem value="all">Alla</SelectItem>
          <SelectItem value="published">Publicerad</SelectItem>
          <SelectItem value="draft">Utkast</SelectItem>
          <SelectItem value="archived">Arkiverad</SelectItem>
        </SelectContent>
      </Select>

      {/* Landskap */}
      <Select value={filters.landskap} onValueChange={(v) => setFilter("landskap", v)}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Landskap" />
        </SelectTrigger>
        <SelectContent className="bg-popover max-h-[300px]">
          <SelectItem value="all">Alla landskap</SelectItem>
          {filterOptions.landskap.map((l) => (
            <SelectItem key={l} value={l}>{l}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Municipality */}
      <Select value={filters.municipality} onValueChange={(v) => setFilter("municipality", v)}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Ort/kommun" />
        </SelectTrigger>
        <SelectContent className="bg-popover max-h-[300px]">
          <SelectItem value="all">Alla orter</SelectItem>
          {filterOptions.municipality.map((m) => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  // ── Mobile filters: compact row + bottom sheet ──
  const mobileFilters = (
    <div className="md:hidden flex gap-2 items-center">
      {/* Compact search */}
      <div className="relative flex-1 min-w-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Sök…"
          value={filters.search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-8 h-9"
        />
        {filters.search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Filter button → sheet */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerTrigger asChild>
          <Button variant="outline" size="sm" className="shrink-0 gap-1.5 h-9">
            <SlidersHorizontal className="h-4 w-4" />
            Filter
            {activeFilterCount > 0 && (
              <Badge variant="default" className="h-4 min-w-4 px-1 text-[10px] leading-none">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </DrawerTrigger>
        <DrawerContent className="bg-background">
          <DrawerHeader>
            <DrawerTitle>Filtrera platser</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-4 space-y-4">
            {/* Status */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Status</label>
              <Select value={filters.status} onValueChange={(v) => setFilter("status", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="all">Alla</SelectItem>
                  <SelectItem value="published">Publicerad</SelectItem>
                  <SelectItem value="draft">Utkast</SelectItem>
                  <SelectItem value="archived">Arkiverad</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Landskap */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Landskap</label>
              <Select value={filters.landskap} onValueChange={(v) => setFilter("landskap", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Landskap" />
                </SelectTrigger>
                <SelectContent className="bg-popover max-h-[300px]">
                  <SelectItem value="all">Alla landskap</SelectItem>
                  {filterOptions.landskap.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Municipality */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Ort / kommun</label>
              <Select value={filters.municipality} onValueChange={(v) => setFilter("municipality", v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Ort/kommun" />
                </SelectTrigger>
                <SelectContent className="bg-popover max-h-[300px]">
                  <SelectItem value="all">Alla orter</SelectItem>
                  {filterOptions.municipality.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DrawerFooter className="flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={() => { resetFilters(); setDrawerOpen(false); }}>
              Rensa filter
            </Button>
            <DrawerClose asChild>
              <Button className="flex-1">Klar</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );

  return (
    <div className="space-y-3">
      {desktopFilters}
      {mobileFilters}

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="flex gap-2 flex-wrap items-center">
          {chips.map((chip) => (
            <Badge key={chip.label} variant="secondary" className="gap-1 pr-1">
              {chip.label}
              <button onClick={chip.onRemove} className="ml-1 hover:text-foreground">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {activeFilterCount > 1 && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs h-6">
              Rensa alla
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
